/**
 * BwC Customers Tab Component
 * Displays customer data from Build with Customer program with rich dashboard design
 */

import { Callout, Checkbox, DirectionalHint, Icon, IconButton, Link, SearchBox, Spinner, SpinnerSize, TooltipHost } from "@fluentui/react";
import { useBoolean } from "@fluentui/react-hooks";
import { LinkRegular } from "@fluentui/react-icons";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import { CustomDataGrid } from "../../SharedComponents/CustomDataGrid/CustomDataGrid";
import { IRows } from "../../SharedComponents/Interfaces/CustomDataGrid.interfaces";
import { useGetBwCCustomersQuery, useGetBwCParticipantNotesBatchMutation, useGetBwCParticipantFeatureRequestsBatchMutation } from "../../slices/BuildWithCustomer.slice";
import {
    CustomerStatusType,
    IBwCCustomerFunnelData,
    IBwCCustomersTabProps
} from "./BuildWithCustomer.interfaces";
import { BWC_CUSTOMERS_STYLES } from "./BwCCustomersTab.styles";
import BwCNotesPanel from "./BwCNotesPanel";
import BwCFeaturePanel from "./BwCFeaturePanel";
import AICompanionSection from "./AICompanionSection";
import FluentDonutChart from "../../ChartComponents/FluentDountChart";
import { CUSTOMER_VIEW_STYLES } from "../CustomerView/CustomerView.styles";
import { getNextColor } from "@fluentui/react-charting";
import CustomCard from "../../SharedComponents/CustomCard";

/**
 * Searchable Multi-Select Dropdown Component
 * - Type to filter options
 * - Select multiple items with checkboxes
 * - Clicking outside closes dropdown without unselecting
 */
interface SearchableMultiSelectProps {
    placeholder: string;
    options: string[];
    selectedKeys: string[];
    onChange: (selectedKeys: string[]) => void;
    width?: string;
}

const SearchableMultiSelect: React.FC<SearchableMultiSelectProps> = ({
    placeholder,
    options,
    selectedKeys,
    onChange,
    width = "100%"
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchText, setSearchText] = useState("");
    const buttonRef = useRef<HTMLDivElement>(null);
    const calloutRef = useRef<HTMLDivElement>(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        if (!isOpen) return;

        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;
            const isClickInsideButton = buttonRef.current?.contains(target);
            const isClickInsideCallout = calloutRef.current?.contains(target);

            if (!isClickInsideButton && !isClickInsideCallout) {
                setIsOpen(false);
                setSearchText("");
            }
        };

        // Use setTimeout to avoid immediate closing when opening
        const timeoutId = setTimeout(() => {
            document.addEventListener("mousedown", handleClickOutside);
        }, 0);

        return () => {
            clearTimeout(timeoutId);
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isOpen]);

    const filteredOptions = useMemo(() => {
        if (!searchText) return options;
        const lowerSearch = searchText.toLowerCase();
        return options.filter((opt) => opt.toLowerCase().includes(lowerSearch));
    }, [options, searchText]);

    const handleToggle = (option: string) => {
        if (selectedKeys.includes(option)) {
            onChange(selectedKeys.filter((k) => k !== option));
        } else {
            onChange([...selectedKeys, option]);
        }
    };

    const displayText = selectedKeys.length > 0
        ? selectedKeys.length === 1
            ? selectedKeys[0]
            : `${selectedKeys.length} selected`
        : placeholder;

    return (
        <div style={{ width, position: "relative" }}>
            <div
                ref={buttonRef}
                onClick={() => setIsOpen(!isOpen)}
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "0 12px",
                    height: "36px",
                    background: "#FFFFFF",
                    border: "1px solid #E1DFDD",
                    borderRadius: "6px",
                    cursor: "pointer",
                    fontSize: "13px",
                    color: selectedKeys.length > 0 ? "#333" : "#666",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap"
                }}
            >
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {displayText}
                </span>
                <Icon iconName="ChevronDown" style={{ fontSize: 12, color: "#605E5C", marginLeft: 8, flexShrink: 0 }} />
            </div>

            {isOpen && (
                <Callout
                    target={buttonRef.current}
                    onDismiss={() => {
                        setIsOpen(false);
                        setSearchText("");
                    }}
                    directionalHint={DirectionalHint.bottomLeftEdge}
                    isBeakVisible={false}
                    gapSpace={2}
                    setInitialFocus={false}
                    styles={{
                        root: { boxShadow: "0 4px 16px rgba(0,0,0,0.12)", borderRadius: "8px" },
                        calloutMain: { borderRadius: "8px", minWidth: 200, maxWidth: 300 }
                    }}
                >
                    <div ref={calloutRef} style={{ padding: "8px" }}>
                        <SearchBox
                            placeholder="Search..."
                            value={searchText}
                            onChange={(_, newValue) => setSearchText(newValue || "")}
                            styles={{
                                root: { marginBottom: 8 },
                                field: { fontSize: 13 }
                            }}
                            autoFocus
                        />
                        <div style={{ maxHeight: 250, overflowY: "auto" }}>
                            {filteredOptions.length === 0 ? (
                                <div style={{ padding: "8px 12px", color: "#666", fontSize: 13 }}>No matches found</div>
                            ) : (
                                filteredOptions.map((option) => (
                                    <div
                                        key={option}
                                        onClick={() => handleToggle(option)}
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            padding: "8px 12px",
                                            cursor: "pointer",
                                            background: selectedKeys.includes(option) ? "#e6f2fb" : "transparent",
                                            borderRadius: 4
                                        }}
                                        onMouseEnter={(e) => {
                                            if (!selectedKeys.includes(option)) {
                                                e.currentTarget.style.background = "#f3f2f1";
                                            }
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.background = selectedKeys.includes(option) ? "#e6f2fb" : "transparent";
                                        }}
                                    >
                                        <Checkbox
                                            checked={selectedKeys.includes(option)}
                                            styles={{ root: { marginRight: 8, pointerEvents: "none" } }}
                                        />
                                        <span style={{ fontSize: 13, color: "#333" }}>{option || "(Blank)"}</span>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </Callout>
            )}
        </div>
    );
};

// Status configuration for badges and funnel bar colors - rich theme design
const STATUS_CONFIG: Record<CustomerStatusType, { background: string; color: string; funnelColor: string }> = {
    Nominated: { background: "#E6E0F7", color: "#5A4796", funnelColor: "#E6E0F7" },
    New: { background: "#E6E0F7", color: "#5A4796", funnelColor: "#E6E0F7" },
    Pending: { background: "linear-gradient(135deg, #F59E0B, #D97706)", color: "#FFFFFF", funnelColor: "#F59E0B" },
    Committed: { background: "linear-gradient(135deg, #3B82F6, #2563EB)", color: "#FFFFFF", funnelColor: "#3B82F6" },
    "In Progress": { background: "#FDE68A", color: "#92400E", funnelColor: "#FDE68A" },
    Completed: { background: "linear-gradient(135deg, #059669, #047857)", color: "#FFFFFF", funnelColor: "#059669" },
    Declined: { background: "linear-gradient(135deg, #EF4444, #DC2626)", color: "#FFFFFF", funnelColor: "#EF4444" }
};

/**
 * Status Badge Component - matching BwC Motion theme styling
 */
const StatusBadge: React.FC<{ status: CustomerStatusType }> = ({ status }) => {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.Nominated;
    return (
        <div
            style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "3px 8px",
                borderRadius: "999px",
                background: config.background,
                color: config.color,
                fontSize: "13px",
                lineHeight: "18px",
                letterSpacing: "0.2px",
                minWidth: "92px"
            }}
        >
            {status}
        </div>
    );
};

/**
 * Stat Card Component - matching first tab design with large icon
 * Optional change prop shows "+N from last week" indicator
 */
const StatCard: React.FC<{
    icon: string;
    iconColor: string;
    iconBgColor: string;
    value: number | string;
    label: string;
    change?: number;
}> = ({ icon, iconColor, iconBgColor, value, label, change }) => (
    <div
        style={{
            background: "#FFFFFF",
            borderRadius: "12px",
            padding: "16px 20px",
            display: "flex",
            alignItems: "center",
            gap: "16px",
            border: "1px solid #E6E9ED",
            flex: 1,
            minWidth: "200px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
            position: "relative"
        }}
    >
        <TooltipHost
            content={label}
            styles={{
                root: {
                    position: "absolute",
                    top: 5,
                    right: 5,
                    display: "inline-flex"
                }
            }}
        >
            <IconButton
                ariaLabel={`Information about ${label}`}
                iconProps={{ iconName: "Info" }}
                styles={{
                    root: {
                        borderRadius: 6
                    }
                }}
            />
        </TooltipHost>
        <div
            style={{
                width: "48px",
                height: "48px",
                borderRadius: "10px",
                background: iconBgColor,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "22px",
                color: iconColor,
                flexShrink: 0
            }}
        >
            <Icon iconName={icon} />
        </div>
        <div style={{ display: "flex", flexDirection: "column" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <span
                    style={{
                        fontSize: "28px",
                        fontWeight: 700,
                        color: "#1A2A45",
                        lineHeight: 1.2
                    }}
                >
                    {value}
                </span>
                {change !== undefined && (
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                            padding: "4px 10px",
                            borderRadius: "20px",
                            background: "linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)",
                            boxShadow: "0 2px 6px rgba(39, 174, 96, 0.2)"
                        }}
                    >
                        <Icon
                            iconName="ChevronUpSmall"
                            style={{
                                fontSize: "12px",
                                fontWeight: 700,
                                color: "#27ae60"
                            }}
                        />
                        <span
                            style={{
                                fontSize: "13px",
                                fontWeight: 600,
                                color: "#27ae60"
                            }}
                        >
                            +{change}
                        </span>
                        <span
                            style={{
                                fontSize: "11px",
                                color: "#9ca3af",
                                marginLeft: "4px"
                            }}
                        >
                            from last week
                        </span>
                    </div>
                )}
            </div>
            <span
                style={{
                    fontSize: "13px",
                    color: "#7f8c8d",
                    marginTop: "2px"
                }}
            >
                {label}
            </span>
        </div>
    </div>
);

/**
 * Donut Chart Filter Component - Status and Last Engaged displayed as donut charts
 * with clickable segments for filtering, matching customer page card design
 */
interface ILastEngagedBucket {
    key: string;
    label: string;
    fullLabel: string;
    count: number;
    color: string;
}

const DonutChartFilters: React.FC<{
    statusData: IBwCCustomerFunnelData[];
    selectedStatuses: string[];
    onStatusClick: (statuses: string[]) => void;
    lastEngagedData: ILastEngagedBucket[];
    selectedLastEngaged: string[];
    onLastEngagedClick: (keys: string[]) => void;
}> = ({ statusData, selectedStatuses, onStatusClick, lastEngagedData, selectedLastEngaged, onLastEngagedClick }) => {

    // Convert status data to donut chart format with click handlers - using Fluent colors
    const statusChartData = useMemo(() => {
        return statusData.map((stage, index) => ({
            legend: stage.stage,
            data: stage.count,
            color: getNextColor(index),
            onClick: () => {
                if (selectedStatuses.includes(stage.stage)) {
                    onStatusClick(selectedStatuses.filter((s) => s !== stage.stage));
                } else {
                    onStatusClick([...selectedStatuses, stage.stage]);
                }
            }
        }));
    }, [statusData, selectedStatuses, onStatusClick]);

    // Convert last engaged data to donut chart format with click handlers - using Fluent colors
    const lastEngagedChartData = useMemo(() => {
        return lastEngagedData.map((bucket, index) => ({
            legend: bucket.label,
            data: bucket.count,
            color: getNextColor(index),
            onClick: () => {
                if (selectedLastEngaged.includes(bucket.key)) {
                    onLastEngagedClick(selectedLastEngaged.filter((k) => k !== bucket.key));
                } else {
                    onLastEngagedClick([...selectedLastEngaged, bucket.key]);
                }
            }
        }));
    }, [lastEngagedData, selectedLastEngaged, onLastEngagedClick]);

    const totalStatusCount = statusData.reduce((sum, s) => sum + s.count, 0);
    const totalLastEngagedCount = lastEngagedData.reduce((sum, b) => sum + b.count, 0);

    return (
        <div className={CUSTOMER_VIEW_STYLES.COMMON_STYLES.CLASS_NAMES.customCardContainer}>
            {/* Status Donut Chart Card */}
            <CustomCard
                cardHeaderTitle="Engagement Status"
                styles={{ root: { flex: 1, minWidth: 350, minHeight: 320 } }}
            >
                {selectedStatuses.length > 0 && (
                    <div style={{ position: "absolute", top: 12, right: 12 }}>
                        <span
                            style={{
                                fontSize: "12px",
                                color: "#0078D4",
                                cursor: "pointer",
                                textDecoration: "underline"
                            }}
                            onClick={() => onStatusClick([])}
                        >
                            Clear Filter ({selectedStatuses.length})
                        </span>
                    </div>
                )}
                <div style={{ minHeight: 280, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <FluentDonutChart
                        key="status chart"
                        data={{
                            chartTitle: "Engagement Status",
                            chartData: statusChartData
                        }}
                        height={230}
                        width={300}
                        innerRadius={50}
                        valueInsideDonut={totalStatusCount}
                        styles={CUSTOMER_VIEW_STYLES.COMMON_STYLES.CSS_STYLES.donutChart}
                    />
                </div>
            </CustomCard>

            {/* Last Engaged Donut Chart Card */}
            <CustomCard
                cardHeaderTitle="Last Engaged (days since latest notes)"
                styles={{ root: { flex: 1, minWidth: 350, minHeight: 320 } }}
            >
                {selectedLastEngaged.length > 0 && (
                    <div style={{ position: "absolute", top: 12, right: 12 }}>
                        <span
                            style={{
                                fontSize: "12px",
                                color: "#059669",
                                cursor: "pointer",
                                textDecoration: "underline"
                            }}
                            onClick={() => onLastEngagedClick([])}
                        >
                            Clear Filter ({selectedLastEngaged.length})
                        </span>
                    </div>
                )}
                <div style={{ minHeight: 280, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <FluentDonutChart
                        key="lastEngaged chart"
                        data={{
                            chartTitle: "Last Engaged",
                            chartData: lastEngagedChartData
                        }}
                        height={230}
                        width={300}
                        innerRadius={50}
                        valueInsideDonut={totalLastEngagedCount}
                        styles={CUSTOMER_VIEW_STYLES.COMMON_STYLES.CSS_STYLES.donutChart}
                    />
                </div>
            </CustomCard>
        </div>
    );
};

/**
 * BwC Customers Tab - Main Component
 */
// LocalStorage key prefix for BwC filter persistence
const BWC_FILTER_STORAGE_PREFIX = "bwc-customers-filter";
const DEFAULT_STATUS_FILTER = ["In Progress"];

export const BwCCustomersTab: React.FC<IBwCCustomersTabProps> = ({ isLoading: externalLoading = false }) => {
    const navigate = useNavigate();

    // Persistent filters - saved to localStorage so users retain their selections across sessions
    const [statusFilter, setStatusFilter] = useLocalStorage<string[]>(`${BWC_FILTER_STORAGE_PREFIX}-status`, DEFAULT_STATUS_FILTER);
    const [regionFilter, setRegionFilter] = useLocalStorage<string[]>(`${BWC_FILTER_STORAGE_PREFIX}-region`, []);
    const [programNameFilter, setProgramNameFilter] = useLocalStorage<string[]>(`${BWC_FILTER_STORAGE_PREFIX}-programName`, []);
    const [cxeLeadFilter, setCxeLeadFilter] = useLocalStorage<string[]>(`${BWC_FILTER_STORAGE_PREFIX}-cxeLead`, []);
    const [groupLeaderFilter, setGroupLeaderFilter] = useLocalStorage<string[]>(`${BWC_FILTER_STORAGE_PREFIX}-groupLeader`, []);
    const [squadLeaderFilter, setSquadLeaderFilter] = useLocalStorage<string[]>(`${BWC_FILTER_STORAGE_PREFIX}-squadLeader`, []);
    const [assignedPMFilter, setAssignedPMFilter] = useLocalStorage<string[]>(`${BWC_FILTER_STORAGE_PREFIX}-assignedPM`, []);
    const [customerNameFilter, setCustomerNameFilter] = useLocalStorage<string[]>(`${BWC_FILTER_STORAGE_PREFIX}-customerName`, []);
    const [lastEngagedFilter, setLastEngagedFilter] = useLocalStorage<string[]>(`${BWC_FILTER_STORAGE_PREFIX}-lastEngaged`, []);

    // On first visit OR when all filters are empty, apply "In Progress" as default
    // Only respect saved empty filters if user has at least one non-empty filter saved
    useEffect(() => {
        // Check if ANY filter key exists in localStorage with non-empty value
        const filterKeys = [
            `${BWC_FILTER_STORAGE_PREFIX}-status`,
            `${BWC_FILTER_STORAGE_PREFIX}-region`,
            `${BWC_FILTER_STORAGE_PREFIX}-programName`,
            `${BWC_FILTER_STORAGE_PREFIX}-cxeLead`,
            `${BWC_FILTER_STORAGE_PREFIX}-groupLeader`,
            `${BWC_FILTER_STORAGE_PREFIX}-squadLeader`,
            `${BWC_FILTER_STORAGE_PREFIX}-assignedPM`,
            `${BWC_FILTER_STORAGE_PREFIX}-customerName`,
            `${BWC_FILTER_STORAGE_PREFIX}-lastEngaged`
        ];

        // Check if any filter has a non-empty value saved
        const hasAnyNonEmptyFilter = filterKeys.some(key => {
            const value = window.localStorage.getItem(key);
            if (value === null) return false;
            try {
                const parsed = JSON.parse(value);
                return Array.isArray(parsed) && parsed.length > 0;
            } catch {
                return false;
            }
        });

        // Only apply default "In Progress" if no filter has any value selected
        if (!hasAnyNonEmptyFilter) {
            setStatusFilter(DEFAULT_STATUS_FILTER);
        }
    }, []); // Run once on mount

    // Last Engaged time buckets with urgency colors
    const LAST_ENGAGED_BUCKETS = [
        { key: "0-7", label: "0-7d", fullLabel: "Active (0-7 days)", minDays: 0, maxDays: 7, color: "#10B981", bgColor: "#ECFDF5", icon: "🟢" },
        { key: "8-14", label: "8-14d", fullLabel: "Recent (8-14 days)", minDays: 8, maxDays: 14, color: "#22C55E", bgColor: "#F0FDF4", icon: "🟡" },
        { key: "15-22", label: "15-22d", fullLabel: "Moderate (15-22 days)", minDays: 15, maxDays: 22, color: "#EAB308", bgColor: "#FEFCE8", icon: "🟠" },
        { key: "23-29", label: "23-29d", fullLabel: "Aging (23-29 days)", minDays: 23, maxDays: 29, color: "#F97316", bgColor: "#FFF7ED", icon: "🟠" },
        { key: "30-59", label: "30-59d", fullLabel: "Stale (30-59 days)", minDays: 30, maxDays: 59, color: "#EF4444", bgColor: "#FEF2F2", icon: "🔴" },
        { key: "60+", label: "60+d", fullLabel: "Inactive (60+ days)", minDays: 60, maxDays: Infinity, color: "#991B1B", bgColor: "#FEE2E2", icon: "⚫" },
        { key: "none", label: "No Notes", fullLabel: "No Notes Recorded", minDays: -1, maxDays: -1, color: "#6B7280", bgColor: "#F3F4F6", icon: "⚪" }
    ];

    // Helper to get bucket key for a customer based on lastModifiedNotesDate
    const getLastEngagedBucket = (lastModifiedNotesDate: string | null | undefined): string => {
        if (!lastModifiedNotesDate) return "none";
        const lastDate = new Date(lastModifiedNotesDate);
        const today = new Date();
        const diffTime = today.getTime() - lastDate.getTime();
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays <= 7) return "0-7";
        if (diffDays <= 14) return "8-14";
        if (diffDays <= 22) return "15-22";
        if (diffDays <= 29) return "23-29";
        if (diffDays <= 59) return "30-59";
        return "60+";
    };

    // Notes panel state
    const [isNotesPanelOpen, { setTrue: openNotesPanel, setFalse: closeNotesPanel }] = useBoolean(false);
    const [isFeaturePanelOpen, { setTrue: openFeaturePanel, setFalse: closeFeaturePanel }] = useBoolean(false);
    const [selectedParticipant, setSelectedParticipant] = useState<{
        programParticipantId: string;
        customerName: string;
        programName: string;
    } | null>(null);
    const [selectedFeatureParticipant, setSelectedFeatureParticipant] = useState<{
        programParticipantId: string;
        customerName: string;
        programName: string;
        featureEvidenceCount: number;
    } | null>(null);

    const handleOpenNotesPanel = (programParticipantId: string, customerName: string, programName: string) => {
        setSelectedParticipant({ programParticipantId, customerName, programName });
        openNotesPanel();
    };

    const handleOpenFeaturePanel = (programParticipantId: string, customerName: string, programName: string, featureEvidenceCount: number) => {
        setSelectedFeatureParticipant({ programParticipantId, customerName, programName, featureEvidenceCount });
        openFeaturePanel();
    };

    // Export state
    const [isExporting, setIsExporting] = useState(false);

    // Fetch data from API - all metrics calculated from customers data
    const { data: customersResponse, isLoading: customersLoading, error: customersError } = useGetBwCCustomersQuery();

    // Batch mutations for export - fetch all notes and feature requests in single calls
    const [getNotesBatch] = useGetBwCParticipantNotesBatchMutation();
    const [getFeatureRequestsBatch] = useGetBwCParticipantFeatureRequestsBatchMutation();

    const isLoading = externalLoading || customersLoading;

    // Extract customers array and Kusto link from response
    const customersData = customersResponse?.customers ?? [];
    const bwCCustomersKustoLink = customersResponse?.bwCCustomersKustoLink ?? "";

    // Extract unique filter options from data
    const filterOptions = useMemo(() => {
        if (!customersData || customersData.length === 0) {
            return {
                regions: [],
                programNames: [],
                cxeLeads: [],
                groupLeaders: [],
                squadLeaders: [],
                assignedPMs: [],
                statuses: [],
                customerNames: []
            };
        }

        const regions = Array.from(new Set(customersData.map((c) => c.customerRegion).filter(Boolean))).sort();
        const programNames = Array.from(new Set(customersData.map((c) => c.programName).filter(Boolean))).sort();
        const cxeLeads = Array.from(new Set(customersData.map((c) => c.cxELead).filter(Boolean))).sort();
        const groupLeaders = Array.from(new Set(customersData.map((c) => c.groupSquadLeader).filter(Boolean))).sort();
        const squadLeaders = Array.from(new Set(customersData.map((c) => c.squadLeader).filter(Boolean))).sort();
        const assignedPMs = Array.from(new Set(customersData.map((c) => c.assignedPM).filter(Boolean))).sort();
        const statuses = Array.from(new Set(customersData.map((c) => c.customerStatus).filter(Boolean))).sort();
        const customerNames = Array.from(new Set(customersData.map((c) => c.customerName).filter(Boolean))).sort();

        return { regions, programNames, cxeLeads, groupLeaders, squadLeaders, assignedPMs, statuses, customerNames };
    }, [customersData]);

    // Filter customers based on all filters
    const filteredCustomers = useMemo(() => {
        if (!customersData || customersData.length === 0) return [];

        return customersData.filter((customer) => {
            const normalizedStatus = customer.customerStatus === "New" ? "Nominated" : customer.customerStatus;
            const matchesStatus = statusFilter.length === 0 || statusFilter.includes(normalizedStatus);
            const matchesRegion = regionFilter.length === 0 || regionFilter.includes(customer.customerRegion);
            const matchesProgramName = programNameFilter.length === 0 || programNameFilter.includes(customer.programName);
            const matchesCxeLead = cxeLeadFilter.length === 0 || cxeLeadFilter.includes(customer.cxELead);
            const matchesGroupLeader =
                groupLeaderFilter.length === 0 || groupLeaderFilter.includes(customer.groupSquadLeader);
            const matchesSquadLeader = squadLeaderFilter.length === 0 || squadLeaderFilter.includes(customer.squadLeader);
            const matchesAssignedPM = assignedPMFilter.length === 0 || assignedPMFilter.includes(customer.assignedPM);
            const matchesCustomerName = customerNameFilter.length === 0 || customerNameFilter.includes(customer.customerName);

            // Last Engaged filter
            const customerBucket = getLastEngagedBucket(customer.lastModifiedNotesDate);
            const matchesLastEngaged = lastEngagedFilter.length === 0 || lastEngagedFilter.includes(customerBucket);

            return (
                matchesStatus &&
                matchesRegion &&
                matchesProgramName &&
                matchesCxeLead &&
                matchesGroupLeader &&
                matchesSquadLeader &&
                matchesAssignedPM &&
                matchesCustomerName &&
                matchesLastEngaged
            );
        });
    }, [
        customersData,
        statusFilter,
        regionFilter,
        programNameFilter,
        cxeLeadFilter,
        groupLeaderFilter,
        squadLeaderFilter,
        assignedPMFilter,
        customerNameFilter,
        lastEngagedFilter
    ]);

    // Calculate metrics from customer data (squad metrics come from API)
    const calculatedMetrics = useMemo(() => {
        const defaultMetrics = {
            totalEngagements: 0,
            uniqueCustomers: 0,
            inProgressUniqueCustomers: 0,
            totalSquads: 0,
            statusCounts: {
                Nominated: 0,
                Pending: 0,
                Committed: 0,
                "In Progress": 0,
                Completed: 0,
                Declined: 0
            },
            last7DaysEngagements: 0,
            last7DaysPrograms: 0,
            last7DaysNewCustomers: 0,
            last7DaysNewInProgressCustomers: 0
        };

        if (!filteredCustomers || filteredCustomers.length === 0) {
            return defaultMetrics;
        }

        // Total customer engagements count
        const totalEngagements = filteredCustomers.length;

        const tenantIdSet = new Set<string>();
        const programIdSet = new Set<string>();
        const inProgressTenantIdSet = new Set<string>();

        // Track first appearance date for each customer (tenantId -> earliest createdOn)
        const customerFirstAppearance = new Map<string, Date>();
        // Track first In Progress appearance for each customer
        const customerFirstInProgress = new Map<string, Date>();

        // Status counts from customers data - dynamically built from API response
        const statusCounts: Record<string, number> = {};

        // Last 7 days calculation
        const now = new Date();
        const last7DaysStart = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

        let last7DaysEngagements = 0;

        // Track programs created in last 7 days (use Set to count distinct programs)
        const last7DaysProgramsSet = new Set<string>();

        filteredCustomers.forEach((customer) => {
            const tenantId = (customer.tenantId ?? "").trim();
            if (tenantId) tenantIdSet.add(tenantId);

            const programId = (customer.programId ?? "").trim();
            if (programId) programIdSet.add(programId);

            // Normalize status: map "New" to "Nominated", default empty to "Unknown"
            let status: string = customer.customerStatus;
            if (status === "New") {
                status = "Nominated";
            } else if (!status || status.trim() === "") {
                status = "Unknown";
            }
            statusCounts[status] = (statusCounts[status] || 0) + 1;

            // Track In Progress unique customers
            if (status === "In Progress" && tenantId) {
                inProgressTenantIdSet.add(tenantId);
            }

            // Track first appearance date for each customer
            if (customer.createdOn && tenantId) {
                const createdDate = new Date(customer.createdOn);
                if (!isNaN(createdDate.getTime())) {
                    const existingDate = customerFirstAppearance.get(tenantId);
                    if (!existingDate || createdDate < existingDate) {
                        customerFirstAppearance.set(tenantId, createdDate);
                    }
                }
            }

            // Track first In Progress appearance for each customer
            if (status === "In Progress" && customer.createdOn && tenantId) {
                const createdDate = new Date(customer.createdOn);
                if (!isNaN(createdDate.getTime())) {
                    const existingDate = customerFirstInProgress.get(tenantId);
                    if (!existingDate || createdDate < existingDate) {
                        customerFirstInProgress.set(tenantId, createdDate);
                    }
                }
            }

            // Last 7 days engagement tracking
            if (customer.createdOn) {
                const createdDate = new Date(customer.createdOn);
                if (createdDate >= last7DaysStart) {
                    last7DaysEngagements++;
                }
            }

            // Last 7 days program tracking
            if (customer.programCreatedOn && programId) {
                const programCreatedDate = new Date(customer.programCreatedOn);
                if (programCreatedDate >= last7DaysStart) {
                    last7DaysProgramsSet.add(programId);
                }
            }
        });

        // Count new customers whose FIRST appearance is within last 7 days
        let last7DaysNewCustomers = 0;
        customerFirstAppearance.forEach((firstDate) => {
            if (firstDate >= last7DaysStart) {
                last7DaysNewCustomers++;
            }
        });

        // Count new In Progress customers whose FIRST In Progress entry is within last 7 days
        let last7DaysNewInProgressCustomers = 0;
        customerFirstInProgress.forEach((firstDate) => {
            if (firstDate >= last7DaysStart) {
                last7DaysNewInProgressCustomers++;
            }
        });

        // Total squads = count of distinct programId
        const totalSquads = programIdSet.size;
        const uniqueCustomers = tenantIdSet.size;
        const inProgressUniqueCustomers = inProgressTenantIdSet.size;

        const last7DaysPrograms = last7DaysProgramsSet.size;

        return {
            totalEngagements,
            uniqueCustomers,
            inProgressUniqueCustomers,
            totalSquads,
            statusCounts,
            last7DaysEngagements,
            last7DaysPrograms,
            last7DaysNewCustomers,
            last7DaysNewInProgressCustomers
        };
    }, [filteredCustomers]);

    // Clear all filters function - resets all filters to empty
    const clearAllFilters = () => {
        setStatusFilter([]);
        setRegionFilter([]);
        setProgramNameFilter([]);
        setCxeLeadFilter([]);
        setGroupLeaderFilter([]);
        setSquadLeaderFilter([]);
        setAssignedPMFilter([]);
        setCustomerNameFilter([]);
        setLastEngagedFilter([]);
    };

    /**
     * Export to CSV with all notes and feature evidence text
     * Uses batch API endpoints to fetch all data in just 2 calls (notes + feature requests)
     */
    const handleExportWithNotes = useCallback(async () => {
        if (filteredCustomers.length === 0) return;

        setIsExporting(true);

        try {
            // Get unique participant IDs for batch fetch
            const participantIds = filteredCustomers
                .map(c => c.programParticipantId)
                .filter((id): id is string => !!id);

            // Fetch all notes and feature requests in parallel (just 2 API calls total)
            const [notesResponse, featureResponse] = await Promise.all([
                getNotesBatch({ programParticipantIds: participantIds }).unwrap(),
                getFeatureRequestsBatch({ programParticipantIds: participantIds }).unwrap()
            ]);

            // Group notes by participant ID
            const notesByParticipant = new Map<string, string[]>();
            for (const note of notesResponse.notes) {
                const existing = notesByParticipant.get(note.programParticipantId) || [];
                // Format: "Subject: Notes text (Modified by on Date)"
                const dateStr = note.modifiedOn ? new Date(note.modifiedOn).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "";
                const noteText = `[${note.subject || "No Subject"}] ${note.notes || ""}${dateStr ? ` (${note.modifiedBy || "Unknown"} on ${dateStr})` : ""}`;
                existing.push(noteText);
                notesByParticipant.set(note.programParticipantId, existing);
            }

            // Group feature evidence by participant ID
            const featuresByParticipant = new Map<string, string[]>();
            for (const feature of featureResponse.featureRequests) {
                const existing = featuresByParticipant.get(feature.programParticipantId) || [];
                // Format: "FeatureEvidenceId: FeatureEvidenceName - FeatureName (Status)"
                const featureText = `[${feature.featureEvidenceId}] ${feature.featureEvidenceName || feature.featureName || "No Name"} - ${feature.featureName || ""} (${feature.featureRequestStatus || "Unknown"})`;
                existing.push(featureText);
                featuresByParticipant.set(feature.programParticipantId, existing);
            }

            // Prepare CSV data
            const headers = [
                "Customer Name",
                "Program Name",
                "Squad Description",
                "Status",
                "Previous Status",
                "Region",
                "Squad Leader",
                "Group Squad Leader",
                "CxE Lead",
                "Assigned PM",
                "Contributing PMs",
                "Participant Stage",
                "Last Updated Date",
                "Feature Evidence Count",
                "All Notes",
                "Feature Evidence"
            ];

            const escapeCSV = (value: string | number) => {
                const str = String(value ?? "");
                // Escape double quotes and wrap in quotes if contains comma, newline, or quotes
                if (str.includes(",") || str.includes("\n") || str.includes("\"")) {
                    return `"${str.replace(/"/g, "\"\"")}"`;
                }
                return str;
            };

            const formatDate = (dateStr: string | null | undefined) => {
                if (!dateStr) return "";
                const date = new Date(dateStr);
                if (isNaN(date.getTime())) return "";
                return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
            };

            const rows = filteredCustomers.map(customer => {
                const participantId = customer.programParticipantId || "";
                const allNotes = notesByParticipant.get(participantId)?.join(" | ") || "";
                const allFeatures = featuresByParticipant.get(participantId)?.join(" | ") || "";

                return [
                    escapeCSV(customer.customerName || ""),
                    escapeCSV(customer.programName || ""),
                    escapeCSV(customer.squadDescription || ""),
                    escapeCSV(customer.customerStatus || ""),
                    escapeCSV(customer.previousStatus || ""),
                    escapeCSV(customer.customerRegion || ""),
                    escapeCSV(customer.squadLeader || ""),
                    escapeCSV(customer.groupSquadLeader || ""),
                    escapeCSV(customer.cxELead || ""),
                    escapeCSV(customer.assignedPM || ""),
                    escapeCSV(customer.contributingPMs || ""),
                    escapeCSV(customer.participantStage || ""),
                    escapeCSV(formatDate(customer.lastModifiedNotesDate)),
                    escapeCSV(customer.featureEvidenceCount || 0),
                    escapeCSV(allNotes),
                    escapeCSV(allFeatures)
                ].join(",");
            });

            const csvContent = [headers.join(","), ...rows].join("\n");

            // Download CSV file
            const timestamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
            const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
            const link = document.createElement("a");
            link.href = URL.createObjectURL(blob);
            link.download = `BwC-Customers-${timestamp}.csv`;
            link.click();
            URL.revokeObjectURL(link.href);
        } catch (error) {
            // Log error but don't expose to user
            console.error("Export failed:", error);
        } finally {
            setIsExporting(false);
        }
    }, [filteredCustomers, getNotesBatch, getFeatureRequestsBatch]);

    // Calculate funnel data from customers - dynamically from API response
    const funnelData: IBwCCustomerFunnelData[] = useMemo(() => {
        const { statusCounts } = calculatedMetrics;
        return Object.entries(statusCounts).map(([stage, count], index) => ({
            stage,
            count,
            color: STATUS_CONFIG[stage as CustomerStatusType]?.funnelColor || getNextColor(index)
        }));
    }, [calculatedMetrics]);

    // Calculate Last Engaged funnel data from filtered customers (responds to status filter)
    const lastEngagedFunnelData: ILastEngagedBucket[] = useMemo(() => {
        return LAST_ENGAGED_BUCKETS.map(bucket => ({
            key: bucket.key,
            label: bucket.label,
            fullLabel: bucket.fullLabel,
            count: filteredCustomers.filter(c => getLastEngagedBucket(c.lastModifiedNotesDate) === bucket.key).length,
            color: bucket.color
        }));
    }, [filteredCustomers]);

    // Table columns configuration - comprehensive view with all required fields
    const columns: GridColDef[] = [
        {
            headerName: "CRM Link",
            field: "crmLinkIcon",
            width: 85,
            sortable: false,
            disableColumnMenu: true,
            renderCell: (params: GridRenderCellParams) => {
                const participantId = params.row.programParticipantId;
                if (!participantId) {
                    return <span style={{ color: "#95a5a6" }}>—</span>;
                }

                const crmUrl = `https://m365crm.crm.dynamics.com/main.aspx?appid=bf381023-a1eb-42ad-b3f5-4ad6a41655fc&pagetype=entityrecord&etn=ems_programparticipents&id=${participantId}`;

                return (
                    <Link
                        style={{
                            color: "rgb(0, 120, 212)",
                            textDecoration: "none",
                            fontSize: 26,
                            cursor: "pointer"
                        }}
                        href={crmUrl}
                        title={crmUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        <LinkRegular />
                    </Link>
                );
            }
        },
        {
            headerName: "Customer Name",
            field: "customerName",
            width: 180,
            sortable: true,
            filterable: true,
            valueGetter: (value: string) => value || "",
            renderCell: (params: GridRenderCellParams) => (
                <a
                    href={params.row.tenantId ? `/customer?id=${params.row.tenantId}&from=buildwithcustomer` : undefined}
                    onClick={(e) => {
                        e.preventDefault();
                        if (params.row.tenantId) {
                            navigate("/customer?id=" + params.row.tenantId + "&from=buildwithcustomer");
                        }
                    }}
                    style={{
                        color: "#2980b9",
                        fontWeight: 600,
                        textDecoration: "none",
                        cursor: params.row.tenantId ? "pointer" : "default"
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.textDecoration = params.row.tenantId ? "underline" : "none")}
                    onMouseLeave={(e) => (e.currentTarget.style.textDecoration = "none")}
                >
                    {params.row.customerName}
                </a>
            )
        },
        {
            headerName: "Status",
            field: "customerStatus",
            width: 130,
            minWidth: 120,
            sortable: true,
            valueGetter: (value: string) => value || "",
            renderCell: (params: GridRenderCellParams) => <StatusBadge status={params.row.customerStatus} />
        },
        {
            headerName: "Region",
            field: "customerRegion",
            width: 100,
            sortable: true,
            valueGetter: (value: string) => value || "",
            renderCell: (params: GridRenderCellParams) => (
                <span style={{ color: params.row.customerRegion ? "#2c3e50" : "#95a5a6" }}>
                    {params.row.customerRegion || "—"}
                </span>
            )
        },
        {
            headerName: "Squad / Program",
            field: "programName",
            minWidth: 200,
            flex: 1,
            sortable: true,
            valueGetter: (value: string) => value || "",
            renderCell: (params: GridRenderCellParams) => (
                <a
                    href={`https://m365crm.crm.dynamics.com/main.aspx?appid=bf381023-a1eb-42ad-b3f5-4ad6a41655fc&pagetype=entityrecord&etn=ems_program&id=${params.row.programId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                        color: "#2980b9",
                        textDecoration: "none"
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.textDecoration = "underline")}
                    onMouseLeave={(e) => (e.currentTarget.style.textDecoration = "none")}
                >
                    {params.row.programName}
                </a>
            )
        },
        {
            headerName: "Squad Description",
            field: "squadDescription",
            width: 200,
            sortable: true,
            valueGetter: (value: string) => value || "",
            renderCell: (params: GridRenderCellParams) => (
                <span
                    style={{
                        color: params.row.squadDescription ? "#2c3e50" : "#95a5a6",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap"
                    }}
                    title={params.row.squadDescription}
                >
                    {params.row.squadDescription || "—"}
                </span>
            )
        },
        {
            headerName: "Squad Leader",
            field: "squadLeader",
            width: 140,
            sortable: true,
            valueGetter: (value: string) => value || "",
            renderCell: (params: GridRenderCellParams) => (
                <span style={{ color: params.row.squadLeader ? "#2c3e50" : "#95a5a6" }}>{params.row.squadLeader || "—"}</span>
            )
        },
        {
            headerName: "Group Squad Leader",
            field: "groupSquadLeader",
            width: 160,
            sortable: true,
            valueGetter: (value: string) => value || "",
            renderCell: (params: GridRenderCellParams) => (
                <span style={{ color: params.row.groupSquadLeader ? "#2c3e50" : "#95a5a6" }}>
                    {params.row.groupSquadLeader || "—"}
                </span>
            )
        },
        {
            headerName: "CxE Lead",
            field: "cxELead",
            width: 140,
            sortable: true,
            valueGetter: (value: string) => value || "",
            renderCell: (params: GridRenderCellParams) => (
                <span style={{ color: params.row.cxELead ? "#2c3e50" : "#95a5a6" }}>{params.row.cxELead || "—"}</span>
            )
        },
        {
            headerName: "Assigned PM",
            field: "assignedPM",
            width: 140,
            sortable: true,
            valueGetter: (value: string) => value || "",
            renderCell: (params: GridRenderCellParams) => (
                <span style={{ color: params.row.assignedPM ? "#2c3e50" : "#95a5a6" }}>{params.row.assignedPM || "—"}</span>
            )
        },
        {
            headerName: "Last Updated Date",
            field: "lastModifiedNotesDate",
            width: 150,
            sortable: true,
            type: "date",
            valueGetter: (value: string) => {
                if (!value) return null;
                const date = new Date(value);
                return isNaN(date.getTime()) ? null : date;
            },
            renderCell: (params: GridRenderCellParams) => {
                const dateValue = params.row.lastModifiedNotesDate;
                if (!dateValue) return <span style={{ color: "#95a5a6" }}>—</span>;
                const date = new Date(dateValue);
                // Check if date is valid
                if (isNaN(date.getTime())) return <span style={{ color: "#95a5a6" }}>—</span>;
                return (
                    <span style={{ color: "#2c3e50", fontSize: "13px" }}>
                        {date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                    </span>
                );
            }
        },
        {
            headerName: "Feature Evidence",
            field: "featureEvidenceCount",
            width: 130,
            sortable: true,
            headerAlign: "center",
            align: "center",
            type: "number",
            renderCell: (params: GridRenderCellParams) => {
                const count = params.row.featureEvidenceCount ?? 0;
                const participantId = params.row.programParticipantId;

                if (count === 0) {
                    return <span style={{ color: "#c8c6c4" }}>—</span>;
                }

                return (
                    <button
                        onClick={() => handleOpenFeaturePanel(
                            participantId,
                            params.row.customerName,
                            params.row.programName,
                            count
                        )}
                        style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 6,
                            padding: "4px 12px",
                            background: "linear-gradient(135deg, #0078d4 0%, #106ebe 100%)",
                            color: "#ffffff",
                            border: "none",
                            borderRadius: 16,
                            fontSize: 13,
                            fontWeight: 600,
                            cursor: "pointer",
                            boxShadow: "0 1px 3px rgba(0,120,212,0.3)",
                            transition: "all 0.2s ease"
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.transform = "scale(1.05)";
                            e.currentTarget.style.boxShadow = "0 2px 6px rgba(0,120,212,0.4)";
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.transform = "scale(1)";
                            e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,120,212,0.3)";
                        }}
                        title="Click to view feature requests"
                    >
                        <Icon iconName="ProductList" style={{ fontSize: 12 }} />
                        {count}
                    </button>
                );
            }
        },
        {
            headerName: "Notes",
            field: "notes",
            minWidth: 80,
            flex: 1,
            sortable: false,
            disableColumnMenu: true,
            headerAlign: "center",
            align: "center",
            renderCell: (params: GridRenderCellParams) => {
                const hasNotes = params.row.notes && params.row.notes.trim();
                const participantId = params.row.programParticipantId;

                if (!hasNotes) {
                    return <span style={{ color: "#c8c6c4" }}>—</span>;
                }

                return (
                    <IconButton
                        iconProps={{ iconName: "QuickNote" }}
                        title="Click to view notes"
                        onClick={() => handleOpenNotesPanel(
                            participantId,
                            params.row.customerName,
                            params.row.programName
                        )}
                        styles={{
                            root: {
                                color: "#0078d4",
                                width: 32,
                                height: 32
                            },
                            icon: {
                                fontSize: 18
                            },
                            rootHovered: {
                                color: "#106ebe",
                                background: "rgba(0,120,212,0.1)"
                            }
                        }}
                    />
                );
            }
        }
    ];

    // Add unique index to each row for proper grid sorting
    const rowsWithIndex = useMemo(() => {
        return filteredCustomers.map((customer, index) => ({
            ...customer,
            _uniqueRowId: `${customer.programParticipantId || ""}_${customer.programId || ""}_${index}`
        }));
    }, [filteredCustomers]);

    const rows: IRows = {
        values: rowsWithIndex,
        count: rowsWithIndex.length
    };

    if (customersError) {
        return (
            <div className={BWC_CUSTOMERS_STYLES.errorContainer}>
                <Icon iconName="ErrorBadge" style={{ fontSize: 48, color: "#e74c3c", marginBottom: 16 }} />
                <div style={{ fontSize: 18, fontWeight: 600, color: "#e74c3c", marginBottom: 8 }}>Error Loading Data</div>
                <div style={{ color: "#7f8c8d" }}>Unable to fetch BwC customer data. Please try again later.</div>
            </div>
        );
    }

    return (
        <div className={BWC_CUSTOMERS_STYLES.tabContainer}>
            {isLoading ? (
                <div className={BWC_CUSTOMERS_STYLES.fullPageLoadingContainer}>
                    <Spinner size={SpinnerSize.large} label="Loading BwC customers..." />
                </div>
            ) : (
                <>
                    {/* Filters Section - matching first tab design */}
                    <div className={BWC_CUSTOMERS_STYLES.filtersCard}>
                        <div className={BWC_CUSTOMERS_STYLES.filtersTitle}>
                            <Icon iconName="Filter" style={{ marginRight: "8px" }} />
                            Filters
                        </div>
                        <div className={BWC_CUSTOMERS_STYLES.filtersRow}>
                            <div className={BWC_CUSTOMERS_STYLES.filterGroup} style={{ width: "200px", minWidth: "180px" }}>
                                <label className={BWC_CUSTOMERS_STYLES.filterLabel}>Customer Name</label>
                                <SearchableMultiSelect
                                    placeholder="Search Customer(s)"
                                    options={filterOptions.customerNames}
                                    selectedKeys={customerNameFilter}
                                    onChange={setCustomerNameFilter}
                                />
                            </div>
                            <div className={BWC_CUSTOMERS_STYLES.filterGroup}>
                                <label className={BWC_CUSTOMERS_STYLES.filterLabel}>Engagement Status</label>
                                <SearchableMultiSelect
                                    placeholder="Search Status(es)"
                                    options={filterOptions.statuses}
                                    selectedKeys={statusFilter}
                                    onChange={setStatusFilter}
                                />
                            </div>
                            <div className={BWC_CUSTOMERS_STYLES.filterGroup}>
                                <label className={BWC_CUSTOMERS_STYLES.filterLabel}>Program Name</label>
                                <SearchableMultiSelect
                                    placeholder="Search Program(s)"
                                    options={filterOptions.programNames}
                                    selectedKeys={programNameFilter}
                                    onChange={setProgramNameFilter}
                                />
                            </div>
                            <div className={BWC_CUSTOMERS_STYLES.filterGroup}>
                                <label className={BWC_CUSTOMERS_STYLES.filterLabel}>CxE Lead</label>
                                <SearchableMultiSelect
                                    placeholder="Search CxE Lead(s)"
                                    options={filterOptions.cxeLeads}
                                    selectedKeys={cxeLeadFilter}
                                    onChange={setCxeLeadFilter}
                                />
                            </div>
                            <div className={BWC_CUSTOMERS_STYLES.filterGroup}>
                                <label className={BWC_CUSTOMERS_STYLES.filterLabel}>Squad Leader</label>
                                <SearchableMultiSelect
                                    placeholder="Search Squad Leader(s)"
                                    options={filterOptions.squadLeaders}
                                    selectedKeys={squadLeaderFilter}
                                    onChange={setSquadLeaderFilter}
                                />
                            </div>
                            <div className={BWC_CUSTOMERS_STYLES.filterGroup}>
                                <label className={BWC_CUSTOMERS_STYLES.filterLabel}>Squad Group Leader</label>
                                <SearchableMultiSelect
                                    placeholder="Search Leader(s)"
                                    options={filterOptions.groupLeaders}
                                    selectedKeys={groupLeaderFilter}
                                    onChange={setGroupLeaderFilter}
                                />
                            </div>
                            <div className={BWC_CUSTOMERS_STYLES.filterGroup}>
                                <label className={BWC_CUSTOMERS_STYLES.filterLabel}>Customer Assigned PM</label>
                                <SearchableMultiSelect
                                    placeholder="Search Assigned PM(s)"
                                    options={filterOptions.assignedPMs}
                                    selectedKeys={assignedPMFilter}
                                    onChange={setAssignedPMFilter}
                                />
                            </div>
                            <div className={BWC_CUSTOMERS_STYLES.filterGroup}>
                                <label className={BWC_CUSTOMERS_STYLES.filterLabel}>Region</label>
                                <SearchableMultiSelect
                                    placeholder="Search Region(s)"
                                    options={filterOptions.regions}
                                    selectedKeys={regionFilter}
                                    onChange={setRegionFilter}
                                />
                            </div>
                            <button className={BWC_CUSTOMERS_STYLES.clearButton} onClick={clearAllFilters}>
                                Clear
                            </button>
                        </div>
                    </div>

                    {/* AI Companion Section - Rich UI for AI Scenarios */}
                    <AICompanionSection
                        customers={customersData}
                        isLoading={customersLoading}
                    />

                    {/* Stats Cards Row - 4 tiles */}
                    <div className={BWC_CUSTOMERS_STYLES.tilesRow}>
                        {/* 1. Unique Customers tile */}
                        <StatCard
                            icon="Group"
                            iconColor="#5A4796"
                            iconBgColor="#E6E0F7"
                            value={calculatedMetrics.uniqueCustomers}
                            label="Unique Customers"
                            change={calculatedMetrics.last7DaysNewCustomers}
                        />
                        {/* 2. Total Engagements tile */}
                        <div
                            style={{
                                background: "linear-gradient(135deg, #FFFFFF 0%, #F8FAFC 100%)",
                                borderRadius: "12px",
                                padding: "16px 20px",
                                display: "flex",
                                alignItems: "center",
                                gap: "16px",
                                border: "1px solid #E6E9ED",
                                flex: 1,
                                minWidth: "280px",
                                boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                                position: "relative"
                            }}
                        >
                            <TooltipHost
                                content="Total Engagements"
                                styles={{
                                    root: {
                                        position: "absolute",
                                        top: 5,
                                        right: 5,
                                        display: "inline-flex"
                                    }
                                }}
                            >
                                <IconButton
                                    ariaLabel="Information about Total Engagements"
                                    iconProps={{ iconName: "Info" }}
                                    styles={{ root: { borderRadius: 6 } }}
                                />
                            </TooltipHost>
                            <div
                                style={{
                                    width: "48px",
                                    height: "48px",
                                    borderRadius: "10px",
                                    background: "#e3f2fd",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontSize: "22px",
                                    color: "#2980b9",
                                    flexShrink: 0
                                }}
                            >
                                <Icon iconName="People" />
                            </div>
                            <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
                                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                                    <span
                                        style={{
                                            fontSize: "28px",
                                            fontWeight: 700,
                                            color: "#1A2A45",
                                            lineHeight: 1.2
                                        }}
                                    >
                                        {calculatedMetrics.totalEngagements}
                                    </span>
                                    {/* Last 7 days count indicator */}
                                    <div
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "4px",
                                            padding: "4px 10px",
                                            borderRadius: "20px",
                                            background: "linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)",
                                            boxShadow: "0 2px 6px rgba(39, 174, 96, 0.2)"
                                        }}
                                    >
                                        <Icon
                                            iconName="ChevronUpSmall"
                                            style={{
                                                fontSize: "12px",
                                                fontWeight: 700,
                                                color: "#27ae60"
                                            }}
                                        />
                                        <span
                                            style={{
                                                fontSize: "13px",
                                                fontWeight: 600,
                                                color: "#27ae60"
                                            }}
                                        >
                                            +{calculatedMetrics.last7DaysEngagements}
                                        </span>
                                        <span
                                            style={{
                                                fontSize: "11px",
                                                color: "#9ca3af",
                                                marginLeft: "4px"
                                            }}
                                        >
                                            from last week
                                        </span>
                                    </div>
                                </div>
                                <span style={{ fontSize: "13px", color: "#7f8c8d", marginTop: "4px" }}>
                                    Total Engagements
                                </span>
                            </div>
                        </div>
                        {/* 4. Number of Programs/Squads tile */}
                        <div
                            style={{
                                background: "linear-gradient(135deg, #FFFFFF 0%, #F8FAFC 100%)",
                                borderRadius: "12px",
                                padding: "16px 20px",
                                display: "flex",
                                alignItems: "center",
                                gap: "16px",
                                border: "1px solid #E6E9ED",
                                flex: 1,
                                minWidth: "240px",
                                boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                                position: "relative"
                            }}
                        >
                            <TooltipHost
                                content="Number of Programs"
                                styles={{
                                    root: {
                                        position: "absolute",
                                        top: 5,
                                        right: 5,
                                        display: "inline-flex"
                                    }
                                }}
                            >
                                <IconButton
                                    ariaLabel="Information about Number of Programs"
                                    iconProps={{ iconName: "Info" }}
                                    styles={{ root: { borderRadius: 6 } }}
                                />
                            </TooltipHost>
                            <div
                                style={{
                                    width: "48px",
                                    height: "48px",
                                    borderRadius: "10px",
                                    background: "#e0f2f1",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontSize: "22px",
                                    color: "#00897b",
                                    flexShrink: 0
                                }}
                            >
                                <Icon iconName="Org" />
                            </div>
                            <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
                                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                                    <span
                                        style={{
                                            fontSize: "28px",
                                            fontWeight: 700,
                                            color: "#1A2A45",
                                            lineHeight: 1.2
                                        }}
                                    >
                                        {calculatedMetrics.totalSquads}
                                    </span>
                                    {/* Last 7 days program count indicator */}
                                    <div
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "4px",
                                            padding: "4px 10px",
                                            borderRadius: "20px",
                                            background: "linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)",
                                            boxShadow: "0 2px 6px rgba(39, 174, 96, 0.2)"
                                        }}
                                    >
                                        <Icon
                                            iconName="ChevronUpSmall"
                                            style={{
                                                fontSize: "12px",
                                                fontWeight: 700,
                                                color: "#27ae60"
                                            }}
                                        />
                                        <span
                                            style={{
                                                fontSize: "13px",
                                                fontWeight: 600,
                                                color: "#27ae60"
                                            }}
                                        >
                                            +{calculatedMetrics.last7DaysPrograms}
                                        </span>
                                        <span
                                            style={{
                                                fontSize: "11px",
                                                color: "#9ca3af",
                                                marginLeft: "4px"
                                            }}
                                        >
                                            from last week
                                        </span>
                                    </div>
                                </div>
                                <span style={{ fontSize: "13px", color: "#7f8c8d", marginTop: "4px" }}>
                                    Number of Programs
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Donut Charts - Click legend items to filter the table (multi-select) */}
                    <DonutChartFilters
                        statusData={funnelData}
                        selectedStatuses={statusFilter}
                        onStatusClick={setStatusFilter}
                        lastEngagedData={lastEngagedFunnelData}
                        selectedLastEngaged={lastEngagedFilter}
                        onLastEngagedClick={setLastEngagedFilter}
                    />

                    {/* Customers Table */}
                    <div className={BWC_CUSTOMERS_STYLES.tableCard}>
                        <CustomDataGrid
                            columns={columns}
                            rows={rows}
                            loading={false}
                            getRowId={(row) => row._uniqueRowId}
                            paginationOptions={{ pageSize: 25, page: 0 }}
                            kustoLink={bwCCustomersKustoLink}
                            TabName="BwC Customers"
                            sortingOrder={["asc", "desc"]}
                            enableUrlParams
                            exportCsvButtonProps={{
                                buttonLabel: "Export",
                                tooltip: "Export to CSV with all notes",
                                onCustomExport: handleExportWithNotes,
                                isExporting
                            }}
                        />
                    </div>
                </>
            )}

            {/* Notes Panel */}
            {selectedParticipant && (
                <BwCNotesPanel
                    isOpen={isNotesPanelOpen}
                    onDismiss={closeNotesPanel}
                    programParticipantId={selectedParticipant.programParticipantId}
                    customerName={selectedParticipant.customerName}
                    programName={selectedParticipant.programName}
                />
            )}

            {/* Feature Requests Panel */}
            {selectedFeatureParticipant && (
                <BwCFeaturePanel
                    isOpen={isFeaturePanelOpen}
                    onDismiss={closeFeaturePanel}
                    programParticipantId={selectedFeatureParticipant.programParticipantId}
                    customerName={selectedFeatureParticipant.customerName}
                    programName={selectedFeatureParticipant.programName}
                    featureEvidenceCount={selectedFeatureParticipant.featureEvidenceCount}
                />
            )}
        </div>
    );
};
